import { addExceptionNotification } from '@mse-reg/app/modules/notification.slice';
import {
  AccountDto,
  AccountInfoDto,
  AccountRequestDto,
  EmailDto,
  IAccountClient,
  IEmailClient,
  ModuleExecutionDto,
  resolver,
  RoleInfoDto,
  RoleType,
  UasDto,
  UserRoleDto,
} from '@mse-reg/clients';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// State
const initialState = {
  accountLoading: false,
  accountsLoading: false,
  requestsLoading: false,
  pendingRequests: [] as AccountRequestDto[],
  rejectedRequests: [] as AccountRequestDto[],
  countPendingAccountRequests: 0,
  userAccounts: [] as AccountDto[],
  selectedAccount: undefined as AccountInfoDto | undefined,
  roleInfo: undefined as { accountId: string; roleInfos: RoleInfoDto } | undefined,
  roleLoading: undefined as RoleType | undefined,
  existingUserLoading: false,
  existingUser: undefined as AccountDto | undefined,
};

export type UserManagementState = Readonly<typeof initialState>;

// Slice
const slice = createSlice({
  name: 'userManagement',
  initialState,
  reducers: {
    setRoleLoading(state, action: PayloadAction<RoleType | null>) {
      state.roleLoading = action.payload ?? undefined;
    },
    setAccountLoading(state, action: PayloadAction<boolean>) {
      state.accountLoading = action.payload;
    },
    setAccountsLoading(state, action: PayloadAction<boolean>) {
      state.accountsLoading = !!action.payload;
    },
    setRequestsLoading(state, action: PayloadAction<boolean>) {
      state.requestsLoading = action.payload;
    },
    setAccounts(state, action: PayloadAction<AccountDto[]>) {
      state.userAccounts = action.payload;
    },
    setPendingRequests(state, action: PayloadAction<AccountRequestDto[]>) {
      state.pendingRequests = action.payload;
      state.countPendingAccountRequests = action.payload?.length ?? 0;
    },
    setRejectedRequests(state, action: PayloadAction<AccountRequestDto[]>) {
      state.rejectedRequests = action.payload;
    },
    reset(state) {
      state.pendingRequests = [];
    },
    setSelectedAccount(state, action: PayloadAction<AccountInfoDto | null>) {
      state.selectedAccount = action.payload ?? undefined;
      state.roleInfo = undefined;
    },
    updateRoles(state, action: PayloadAction<{ accountId: string; roleInfos: RoleInfoDto | null }>) {
      state.roleInfo = action.payload.roleInfos
        ? { accountId: action.payload.accountId, roleInfos: action.payload.roleInfos }
        : undefined;
    },
    setExistingUserLoading(state, action: PayloadAction<boolean>) {
      state.existingUserLoading = action.payload;
    },
    setExistingUser(state, action: PayloadAction<AccountDto | null>) {
      state.existingUser = action.payload ?? undefined;
    },
  },
});

export const userManagementReducer = slice.reducer;
export const userManagementActions = slice.actions;

// Component-scoped actions factory
type ThunkFn<T = void> = (dispatch: any, getState: () => any) => Promise<T>;

const initActions = (componentDispatch: any, accountClient: IAccountClient, emailClient: IEmailClient) => {
  const { actions } = slice;

  const loadPendingRequestsAsync = (): ThunkFn => async (dispatch) => {
    dispatch(actions.setRequestsLoading(true));
    try {
      const result = (await accountClient.getRequests()) ?? [];
      dispatch(actions.setPendingRequests(result));
      dispatch(actions.setRequestsLoading(false));
    } catch (e) {
      dispatch(actions.setRequestsLoading(false));
      dispatch(addExceptionNotification(e, 'An error occurred while loading account requests.'));
    }
  };

  const loadRejectedRequestsAsync = (): ThunkFn => async (dispatch) => {
    dispatch(actions.setRequestsLoading(true));
    try {
      const result = (await accountClient.getBlockedRequests()) ?? [];
      dispatch(actions.setRejectedRequests(result));
      dispatch(actions.setRequestsLoading(false));
    } catch (e) {
      dispatch(actions.setRequestsLoading(false));
      dispatch(addExceptionNotification(e, 'An error occurred while loading accounts.'));
    }
  };

  const loadAccountsAsync = (): ThunkFn => async (dispatch) => {
    dispatch(actions.setAccountsLoading(true));
    try {
      const result = (await accountClient.getAccounts(true)) ?? [];
      dispatch(actions.setAccounts(result));
      dispatch(actions.setAccountsLoading(false));
    } catch (e) {
      dispatch(actions.setAccountsLoading(false));
      dispatch(addExceptionNotification(e, 'An error occurred while loading accounts.'));
    }
  };

  const loadAccountAsync =
    (id: string): ThunkFn =>
    async (dispatch) => {
      dispatch(actions.setAccountLoading(true));
      try {
        const result = await accountClient.getAccountInfo(id);
        const roleInfo = await accountClient.getRoleInfos(id);
        dispatch(actions.setSelectedAccount(result));
        dispatch(actions.updateRoles({ accountId: id, roleInfos: roleInfo }));
        dispatch(actions.setAccountLoading(false));
      } catch (e) {
        dispatch(actions.setAccountLoading(false));
        dispatch(addExceptionNotification(e, 'An error occurred while loading the account.'));
      }
    };

  const deleteEmailAsync =
    (emailDto: EmailDto): ThunkFn =>
    async (dispatch) => {
      dispatch(actions.setAccountLoading(true));
      try {
        await emailClient.delete(emailDto.id);
        await dispatch(loadAccountAsync(emailDto.userId));
        dispatch(actions.setAccountLoading(false));
      } catch (e) {
        dispatch(actions.setAccountLoading(false));
        dispatch(addExceptionNotification(e, 'An error occurred while deleting the email.'));
      }
    };

  const updateAsync =
    (account: AccountInfoDto): ThunkFn =>
    async (dispatch) => {
      dispatch(actions.setAccountLoading(true));
      try {
        await accountClient.update(account);
        await dispatch(loadAccountAsync(account.id));
        await dispatch(loadAccountsAsync());
        dispatch(actions.setAccountLoading(false));
      } catch (e) {
        dispatch(actions.setAccountLoading(false));
        dispatch(addExceptionNotification(e, 'An error occurred while updating the account.'));
      }
    };

  const deleteAsync =
    (account: AccountInfoDto): ThunkFn =>
    async (dispatch) => {
      dispatch(actions.setAccountLoading(true));
      try {
        await accountClient.delete(account.id);
        dispatch(actions.setSelectedAccount(null));
        await dispatch(loadAccountsAsync());
        dispatch(actions.setAccountLoading(false));
      } catch (e) {
        dispatch(actions.setAccountLoading(false));
        dispatch(addExceptionNotification(e, 'An error occurred while deleting the account.'));
      }
    };

  const addRoleAsync =
    (account: AccountInfoDto, roleType: RoleType): ThunkFn =>
    async (dispatch) => {
      dispatch(actions.setRoleLoading(roleType));
      dispatch(actions.setAccountLoading(true));
      try {
        await accountClient.addRole(account.id, roleType);
        await dispatch(loadAccountAsync(account.id));
        await dispatch(loadAccountsAsync());
        const roleInfo = await accountClient.getRoleInfos(account.id);
        dispatch(actions.updateRoles({ accountId: account.id, roleInfos: roleInfo }));
        dispatch(actions.setAccountLoading(false));
        dispatch(actions.setRoleLoading(null));
      } catch (e) {
        dispatch(actions.setAccountLoading(false));
        dispatch(actions.setRoleLoading(null));
        dispatch(addExceptionNotification(e, 'An error occurred while activating the account.'));
      }
    };

  const deleteRoleAsync =
    (account: AccountInfoDto, roleType: RoleType): ThunkFn =>
    async (dispatch) => {
      dispatch(actions.setRoleLoading(roleType));
      dispatch(actions.setAccountLoading(true));
      try {
        await accountClient.deleteRole(account.id, roleType);
        await dispatch(loadAccountAsync(account.id));
        await dispatch(loadAccountsAsync());
        const roleInfo = await accountClient.getRoleInfos(account.id);
        dispatch(actions.updateRoles({ accountId: account.id, roleInfos: roleInfo }));
        dispatch(actions.setRoleLoading(null));
        dispatch(actions.setAccountLoading(false));
      } catch (e) {
        dispatch(actions.setAccountLoading(false));
        dispatch(actions.setRoleLoading(null));
        dispatch(addExceptionNotification(e, 'An error occurred while activating the account.'));
      }
    };

  const activateRoleAsync =
    (role: UserRoleDto): ThunkFn =>
    async (dispatch) => {
      dispatch(actions.setRoleLoading(role.roleType));
      try {
        await accountClient.activateRole(role.id);
        await dispatch(loadAccountAsync(role.userId));
        const roleInfo = await accountClient.getRoleInfos(role.userId);
        dispatch(actions.updateRoles({ accountId: role.userId, roleInfos: roleInfo }));
        dispatch(actions.setRoleLoading(null));
      } catch (e) {
        dispatch(actions.setRoleLoading(null));
        dispatch(addExceptionNotification(e, `An error occurred while activating the role ${role.roleType}.`));
      }
    };

  const deactivateRoleAsync =
    (role: UserRoleDto): ThunkFn =>
    async (dispatch) => {
      dispatch(actions.setRoleLoading(role.roleType));
      try {
        await accountClient.deactivateRole(role.id);
        await dispatch(loadAccountAsync(role.userId));
        await dispatch(loadAccountsAsync());
        const roleInfo = await accountClient.getRoleInfos(role.userId);
        dispatch(actions.updateRoles({ accountId: role.userId, roleInfos: roleInfo }));
        dispatch(actions.setRoleLoading(null));
      } catch (e) {
        dispatch(actions.setRoleLoading(null));
        dispatch(addExceptionNotification(e, `An error occurred while deactivating the role ${role.roleType}.`));
      }
    };

  const addUasAsync =
    (role: UserRoleDto, uas: UasDto): ThunkFn =>
    async (dispatch) => {
      dispatch(actions.setRoleLoading(role.roleType));
      try {
        await accountClient.addUas(role.id, uas.id);
        const roleInfo = await accountClient.getRoleInfos(role.userId);
        await dispatch(loadAccountAsync(role.userId));
        dispatch(actions.updateRoles({ accountId: role.userId, roleInfos: roleInfo }));
        dispatch(actions.setRoleLoading(null));
      } catch (e) {
        dispatch(actions.setRoleLoading(null));
        dispatch(
          addExceptionNotification(
            e,
            `An error occurred while adding the uas ${uas.code} to the role ${role.roleType}.`,
          ),
        );
      }
    };

  const removeUasAsync =
    (role: UserRoleDto, uas: UasDto): ThunkFn =>
    async (dispatch) => {
      dispatch(actions.setRoleLoading(role.roleType));
      try {
        await accountClient.removeUas(role.id, uas.id);
        const roleInfo = await accountClient.getRoleInfos(role.userId);
        await dispatch(loadAccountAsync(role.userId));
        dispatch(actions.updateRoles({ accountId: role.userId, roleInfos: roleInfo }));
        dispatch(actions.setRoleLoading(null));
      } catch (e) {
        dispatch(actions.setRoleLoading(null));
        dispatch(
          addExceptionNotification(
            e,
            `An error occurred while removing the uas ${uas.code} from the role ${role.roleType}.`,
          ),
        );
      }
    };

  const addLecturerForAsync =
    (role: UserRoleDto, moduleExecution: ModuleExecutionDto): ThunkFn =>
    async (dispatch) => {
      dispatch(actions.setRoleLoading(role.roleType));
      try {
        await accountClient.addLecturerFor(role.id, moduleExecution.id);
        const roleInfo = await accountClient.getRoleInfos(role.userId);
        await dispatch(loadAccountAsync(role.userId));
        dispatch(actions.updateRoles({ accountId: role.userId, roleInfos: roleInfo }));
        dispatch(actions.setRoleLoading(null));
      } catch (e) {
        dispatch(actions.setRoleLoading(null));
        dispatch(
          addExceptionNotification(
            e,
            `An error occurred while adding the module execution ${moduleExecution.code} to the role ${role.roleType}.`,
          ),
        );
      }
    };

  const removeLecturerForAsync =
    (role: UserRoleDto, moduleExecution: ModuleExecutionDto): ThunkFn =>
    async (dispatch) => {
      dispatch(actions.setRoleLoading(role.roleType));
      try {
        await accountClient.removeLecturerFor(role.id, moduleExecution.id);
        const roleInfo = await accountClient.getRoleInfos(role.userId);
        await dispatch(loadAccountAsync(role.userId));
        dispatch(actions.updateRoles({ accountId: role.userId, roleInfos: roleInfo }));
        dispatch(actions.setRoleLoading(null));
      } catch (e) {
        dispatch(actions.setRoleLoading(null));
        dispatch(
          addExceptionNotification(
            e,
            `An error occurred while removing the module execution ${moduleExecution.code} from the role ${role.roleType}.`,
          ),
        );
      }
    };

  const activateAsync =
    (activationId: number, roleTypes: RoleType[]): ThunkFn =>
    async (dispatch) => {
      dispatch(actions.setRequestsLoading(true));
      try {
        await accountClient.activate(activationId, roleTypes);
        const accounts = (await accountClient.getAccounts(true)) ?? [];
        dispatch(actions.setAccounts(accounts));
        const pending = (await accountClient.getRequests()) ?? [];
        dispatch(actions.setPendingRequests(pending));
        dispatch(actions.setRequestsLoading(false));
      } catch (e) {
        dispatch(actions.setRequestsLoading(false));
        dispatch(addExceptionNotification(e, 'An error occurred while activating the account.'));
      }
    };

  const blockRequestAsync =
    (activationId: number): ThunkFn =>
    async (dispatch) => {
      dispatch(actions.setAccountsLoading(true));
      try {
        await accountClient.blockRequest(activationId);
        const blocked = (await accountClient.getBlockedRequests()) ?? [];
        dispatch(actions.setRejectedRequests(blocked));
        const pending = (await accountClient.getRequests()) ?? [];
        dispatch(actions.setPendingRequests(pending));
        dispatch(actions.setAccountsLoading(false));
      } catch (e) {
        dispatch(actions.setAccountsLoading(false));
        dispatch(addExceptionNotification(e, 'An error occurred while blocking the account request.'));
      }
    };

  const deleteRequestAsync =
    (activationId: number): ThunkFn =>
    async (dispatch) => {
      dispatch(actions.setAccountsLoading(true));
      try {
        await accountClient.deleteRequest(activationId);
        const blocked = (await accountClient.getBlockedRequests()) ?? [];
        dispatch(actions.setRejectedRequests(blocked));
        const pending = (await accountClient.getRequests()) ?? [];
        dispatch(actions.setPendingRequests(pending));
        dispatch(actions.setAccountsLoading(false));
      } catch (e) {
        dispatch(actions.setAccountsLoading(false));
        dispatch(addExceptionNotification(e, 'An error occurred while deleting the account request.'));
      }
    };

  const unblockRequestAsync =
    (activationId: number): ThunkFn =>
    async (dispatch) => {
      dispatch(actions.setAccountsLoading(true));
      try {
        await accountClient.unblockRequest(activationId);
        const blocked = (await accountClient.getBlockedRequests()) ?? [];
        dispatch(actions.setRejectedRequests(blocked));
        const pending = (await accountClient.getRequests()) ?? [];
        dispatch(actions.setPendingRequests(pending));
        dispatch(actions.setAccountsLoading(false));
      } catch (e) {
        dispatch(actions.setAccountsLoading(false));
        dispatch(addExceptionNotification(e, 'An error occurred while unblocking the account request.'));
      }
    };

  const emailAlreadyExistsAsync = async (userId: string, email: string) => {
    return await accountClient.checkEmail(userId, email);
  };

  const registerAsync = async () => {
    await accountClient.register();
  };

  const loadExistingUserAsync =
    (activationId: number): ThunkFn =>
    async (dispatch) => {
      dispatch(actions.setExistingUserLoading(true));
      try {
        const user = await accountClient.getExistingUser(activationId);
        dispatch(actions.setExistingUser(user));
        dispatch(actions.setExistingUserLoading(false));
      } catch (e) {
        dispatch(actions.setExistingUserLoading(false));
        dispatch(addExceptionNotification(e, 'An error occurred while checking for existing account.'));
      }
    };

  return Object.freeze({
    emailAlreadyExistsAsync,
    loadExistingUserAsync: async (activationId: number) => await componentDispatch(loadExistingUserAsync(activationId)),
    deleteRoleAsync: async (accountDto: AccountInfoDto, roleType: RoleType) =>
      await componentDispatch(deleteRoleAsync(accountDto, roleType)),
    addRoleAsync: async (accountDto: AccountInfoDto, roleType: RoleType) =>
      await componentDispatch(addRoleAsync(accountDto, roleType)),
    reset: () => componentDispatch(actions.reset()),
    deleteEmailAsync: async (emailDto: EmailDto): Promise<void> => await componentDispatch(deleteEmailAsync(emailDto)),
    loadAccountAsync: async (id: string): Promise<void> => await componentDispatch(loadAccountAsync(id)),
    updateAsync: async (accountDto: AccountInfoDto): Promise<void> => await componentDispatch(updateAsync(accountDto)),
    addUasAsync: async (userRole: UserRoleDto, uas: UasDto) => await componentDispatch(addUasAsync(userRole, uas)),
    removeUasAsync: async (userRole: UserRoleDto, uas: UasDto) =>
      await componentDispatch(removeUasAsync(userRole, uas)),
    addLecturerForAsync: async (userRole: UserRoleDto, moduleExecution: ModuleExecutionDto) =>
      await componentDispatch(addLecturerForAsync(userRole, moduleExecution)),
    removeLecturerForAsync: async (userRole: UserRoleDto, moduleExecution: ModuleExecutionDto) =>
      await componentDispatch(removeLecturerForAsync(userRole, moduleExecution)),
    activateRoleAsync: async (userRole: UserRoleDto) => await componentDispatch(activateRoleAsync(userRole)),
    deactivateRoleAsync: async (userRole: UserRoleDto) => await componentDispatch(deactivateRoleAsync(userRole)),
    selectAccount: (account: AccountInfoDto | null) => componentDispatch(actions.setSelectedAccount(account)),
    loadPendingRequestsAsync: async (): Promise<void> => await componentDispatch(loadPendingRequestsAsync()),
    loadAccountsAsync: async (): Promise<void> => await componentDispatch(loadAccountsAsync()),
    loadRejectedRequestsAsync: async (): Promise<void> => await componentDispatch(loadRejectedRequestsAsync()),
    activateAsync: async (activationId: number, roleTypes: RoleType[]) =>
      await componentDispatch(activateAsync(activationId, roleTypes)),
    blockRequestAsync: async (activationId: number) => await componentDispatch(blockRequestAsync(activationId)),
    unblockRequestAsync: async (activationId: number) => await componentDispatch(unblockRequestAsync(activationId)),
    deleteRequestAsync: async (activationId: number) => await componentDispatch(deleteRequestAsync(activationId)),
    deleteAsync: async (account: AccountInfoDto) => await componentDispatch(deleteAsync(account)),
    registerAsync,
  });
};

export type UserManagementActions = ReturnType<typeof initActions>;

export const userManagementModule = {
  initActions: (dispatch: any) =>
    initActions(
      dispatch,
      resolver.resolve<IAccountClient>('IAccountClient'),
      resolver.resolve<IEmailClient>('IEmailClient'),
    ),
  reducer: userManagementReducer,
};
